import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getUsageDoc, getTierConfig } from '../utils/accountStorage';
import { TIER_DEFAULTS, TIER_NAMES, TIER_PRICES, DEFAULT_TIER } from '../config/tierDefaults';

const FEATURE_COPY = {
  maxReports:           { name: 'Reports' },
  maxExpensesPerReport: { name: 'Expenses per report' },
  customCategories:     { name: 'Custom categories' },
  canCombineReports:    { name: 'Combine reports on export' },
  canShare:             { name: 'Report sharing' },
  canShareEdit:         { name: 'Edit access sharing' },
  taxReport:            { name: 'Tax Report export' },
  auditTrail:           { name: 'Audit trail in exports' },
  groupBy:              { name: 'Group by' },
  imageStorage:         { name: 'Receipt image storage' },
  scansPerMonth:        { name: 'Receipt scanning' },
  aiReconcile:          { name: 'AI transaction reconciliation' },
  aiBookkeeper:         { name: 'AI Bookkeeper' },
  aiQueriesPerWeek:     { name: 'AI queries per week' },
  aiTokensInPerWeek:    { name: 'AI prompt tokens per week' },
  aiTokensOutPerWeek:   { name: 'AI completion tokens per week' },
  aiToolCallsPerWeek:   { name: 'AI tool calls per week' },
  aiToolCallsPerMonth:  { name: 'AI tool calls per month' },
};

function getWeekKey() {
  const now = new Date();
  const day = now.getUTCDay();
  const diffToMonday = day === 0 ? -6 : 1 - day;
  const monday = new Date(now);
  monday.setUTCDate(now.getUTCDate() + diffToMonday);
  return monday.toISOString().slice(0, 10);
}


export function getNextTier(tier) {
  if (tier === 'basic')   return 'starter';
  if (tier === 'starter') return 'pro';
  return null;
}

export function getNextTierThatAllowsFeature(tier, feature, liveConfig) {
  // just placeholder for now.  We actually need to check what is the lowest tier that allows this feature
  let tierToCheck = tier;
  let nextTier = null;

  while ((nextTier = getNextTier(tierToCheck)))  {
    let nextLimits = getMergedLimits(nextTier, liveConfig)
    console.log("checking ", nextTier, " for feature=", feature, " got nextLimits:", nextLimits, " nextLimits[feature] is ", nextLimits[feature])
    if (nextLimits[feature]) return nextTier
    tierToCheck=nextTier
  } 
  return null;
}

export function getNextTierThatSupportsExportFormat(tier, format, liveConfig) {
  // just placeholder for now.  We actually need to check what is the lowest tier that allows this feature
  let tierToCheck = tier;
  let nextTier = null;

  while ((nextTier = getNextTier(tierToCheck)))  {
    let nextLimits = getMergedLimits(nextTier, liveConfig)
    console.log("checking ", nextTier, " for format=", format, " got nextLimits.exportFormats:", nextLimits.exportFormats, " nextLimits[format] is ", nextLimits[format])
    if (nextLimits.exportFormats.includes(format)) {
      console.log(format, " is supported!")
      return nextTier
    }
    tierToCheck=nextTier
  } 
  console.log("THis is weird....this is not supported at any tier.")
  return null;
}

function getMergedLimits(tier, liveConfig) {
  const defaults = TIER_DEFAULTS[tier] || TIER_DEFAULTS[DEFAULT_TIER];
  const live     = (liveConfig && liveConfig[tier]) || {};
  return { ...defaults, ...live };
}

export function useTier() {
  const { user, account } = useAuth();
  const uid = user?.uid ?? null;

  const [tierConfig, setTierConfig] = useState(null);
  const [usage,      setUsage]      = useState(null);
  const [loading,    setLoading]    = useState(true);

  useEffect(() => {
    if (!uid) { setLoading(false); return; }
    let cancelled = false;
    Promise.all([getTierConfig(), getUsageDoc(uid)]).then(([config, usageData]) => {
      if (cancelled) return;
      setTierConfig(config);
      setUsage(usageData);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [uid]);

  // Called by App.js after a successful scan so the counter refreshes
  const refreshUsage = useCallback(async () => {
    if (!uid) return;
    const fresh = await getUsageDoc(uid);
    setUsage(fresh);
  }, [uid]);

  const tier   = account?.tier || DEFAULT_TIER;
  const limits = getMergedLimits(tier, tierConfig);

  const weekKey      = getWeekKey();
  const monthKey     = new Date().toISOString().slice(0, 7);
  const scansUsed    = (usage && usage.monthKey === monthKey) ? (usage.scansThisMonth || 0) : 0;
  const extraCredits = account?.scanCreditsExtra || 0;
  const totalScansAllowed = limits.scansPerMonth + extraCredits;
  const scansRemaining    = totalScansAllowed === -1 ? Infinity : Math.max(0, totalScansAllowed - scansUsed);
  const canScan           = scansRemaining > 0;

  const toolCallsWeekUsed   = (usage && usage.toolCallsWeekKey  === weekKey)  ? (usage.toolCallsWeek  ?? 0) : 0;
  const toolCallsMonthUsed  = (usage && usage.toolCallsMonthKey === monthKey) ? (usage.toolCallsMonth ?? 0) : 0;
  const maxToolCallsWeek    = limits.aiToolCallsPerWeek  ?? 20;
  const maxToolCallsMonth   = limits.aiToolCallsPerMonth ?? 60;
  const toolCallsWeekRemaining  = maxToolCallsWeek  === -1 ? Infinity : Math.max(0, maxToolCallsWeek  - toolCallsWeekUsed);
  const toolCallsMonthRemaining = maxToolCallsMonth === -1 ? Infinity : Math.max(0, maxToolCallsMonth - toolCallsMonthUsed);

  const checkLimit = useCallback((feature, currentCount = null) => {
    const copy      = FEATURE_COPY[feature];
    const featureName = copy?.name || feature;
    const nextTier  = getNextTier(tier);
    const nextLimits = nextTier ? getMergedLimits(nextTier, tierConfig) : null;

    // Special case: scan limit
    if (feature === 'scansPerMonth') {
      if (canScan) return { allowed: true };
      const nextScans = nextLimits?.scansPerMonth;
      return {
        allowed:        false,
        featureName,
        message:        `You've used all ${totalScansAllowed} scan${totalScansAllowed !== 1 ? 's' : ''} for ${monthKey} on your ${TIER_NAMES[tier]} plan.`,
        upgradeTo:      nextTier,
        upgradeMessage: nextTier
          ? `Upgrade to ${TIER_NAMES[nextTier]} (${TIER_PRICES[nextTier]}) for ${nextScans} scans/month.`
          : 'You are on the highest plan.',
      };
    }

    const limitVal = limits[feature];

    // Boolean feature
    if (typeof limitVal === 'boolean') {
      if (limitVal) return { allowed: true };
      return {
        allowed:        false,
        featureName,
        message:        `${featureName} is not available on your ${TIER_NAMES[tier]} plan.`,
        upgradeTo:      nextTier,
        upgradeMessage: nextTier
          ? `Upgrade to ${TIER_NAMES[nextTier]} (${TIER_PRICES[nextTier]}) to unlock ${featureName.toLowerCase()}.`
          : 'This feature requires the Pro plan.',
      };
    }

    // Numeric feature (-1 = unlimited)
    if (limitVal === -1 || limitVal == null) return { allowed: true };
    if (currentCount !== null && currentCount >= limitVal) {
      const nextVal = nextLimits?.[feature];
      const nextLabel = nextVal === -1 ? 'unlimited' : (nextVal != null ? String(nextVal) : 'more');
      return {
        allowed:        false,
        featureName,
        message:        `Your ${TIER_NAMES[tier]} plan allows up to ${limitVal} ${featureName.toLowerCase()}. You've reached the limit.`,
        upgradeTo:      nextTier,
        upgradeMessage: nextTier
          ? `Upgrade to ${TIER_NAMES[nextTier]} (${TIER_PRICES[nextTier]}) for ${nextLabel} ${featureName.toLowerCase()}.`
          : 'You are on the highest plan.',
      };
    }

    return { allowed: true };
  }, [tier, limits, tierConfig, canScan, totalScansAllowed, monthKey]);

  return {
    tier,
    limits,
    tierConfig,
    scansUsed,
    scansRemaining,
    totalScansAllowed,
    canScan,
    toolCallsWeekUsed,
    toolCallsMonthUsed,
    toolCallsWeekRemaining,
    toolCallsMonthRemaining,
    maxToolCallsWeek,
    maxToolCallsMonth,
    checkLimit,
    refreshUsage,
    loading,
  };
}
